import { useQuery } from '@tanstack/react-query';
import { memo, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import VideoList from '../../components/VideoList';
import { useBucketContent } from '../../hooks/useBucketContent';
import { useUefatvOptions } from '../../store';

import { getLink } from '@apps/uefa-tv/utils/utility';
import { useUser } from '@shared/hooks/useUser';
import { getVideoQuery } from '@shared/queries/uefatv';
import type { UefatvEditorialContent } from '@shared-types/uefatv.types';
import { mapEditorialContent } from '@utilities/uefatv';

const VideoView = ({ videoId }: { videoId: string }) => {
  const { logged } = useUser();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    ...getVideoQuery(videoId, logged),
    enabled: !!videoId,
    select: (data) => ({
      ...data,
      items: [mapEditorialContent(data)],
    }),
  });
  const video = useMemo(() => data?.items?.find((c) => c.id === videoId), [data?.items, videoId]);
  const setCurrentVideo = (video: UefatvEditorialContent) => navigate(getLink(video));

  if (isLoading) return null;

  return <VideoList video={video} setCurrentVideo={setCurrentVideo} />;
};

const BucketView = ({ videoId, bucketId, lastSeen }: { videoId: string; bucketId: string; lastSeen?: string }) => {
  const { options } = useUefatvOptions();
  const navigate = useNavigate();

  const { logged } = useUser();
  const { data, isLoading, loadMoreContent, hasNextPage } = useBucketContent(
    bucketId,
    logged,
    options.bpp,
    options.rpp,
    lastSeen,
  );
  const handleScrolled = () => {
    loadMoreContent();
  };

  const video = useMemo(() => data?.items?.find((c) => c.id === videoId), [data?.items, videoId]);
  if (!video) return <VideoView videoId={videoId} />;

  const setCurrentVideo = (video: UefatvEditorialContent) => navigate(getLink(video, bucketId));

  if (isLoading) return null;

  return (
    <VideoList
      video={video}
      container={data}
      setCurrentVideo={setCurrentVideo}
      onScrolled={handleScrolled}
      hasNextPage={hasNextPage}
    />
  );
};

const Vod = () => {
  const { videoId } = useParams();
  const [queryParams] = useSearchParams();
  const bucketId = queryParams.get('bucketId');
  const lastSeen = queryParams.get('lastSeen');

  return bucketId ? (
    <BucketView videoId={videoId} bucketId={bucketId} lastSeen={lastSeen} />
  ) : (
    <VideoView videoId={videoId} />
  );
};

export default memo(Vod);
